import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import TabNavigation from "../components/common/ui/TabNavigation";
import MediaPlanContent from "./media/MediaPlanContent";
import ResumeManager from "./HiringStages/ResumeScreeningPage";
import { jobService } from "../services/job.service";
import GradientDetailLayout from "../components/common/ui/GradientLayout";
import PreScreeningPage from "./HiringStages/PreScreeningPage";
import FinalInterviewPage from './HiringStages/FinalInterviewPage';
import OfferLetterPage from './HiringStages/OfferLetterPage';
import PsychoanalyticalPage from "./HiringStages/PsychoanalyticalPage";
import JobDescription from "./jobDescription/JobDescription";
import PipelineBuilder from "./HiringStages/PipelineBuilder";
import PipelineStageTab from "./HiringStages/PipelineStageTab";
import JobQuestionnaire from "./HiringStages/JobQuestionnaire";
import api from "../services/api";

const STORAGE_KEY = 'jobDescription';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || "Job Description";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputContent, setInputContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [effectivePipeline, setEffectivePipeline] = useState(null); // { isCustom, stages }


  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Fetch job data with localStorage sync
  const fetchJobData = async () => {
    try {
      setLoading(true);
      
      // Try to get data from localStorage first
      const cachedData = localStorage.getItem(`${STORAGE_KEY}_${id}`);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setJobData(parsed);
        setGeneratedContent(parsed.generatedContent);
      }

      // Fetch fresh data from API
      const response = await jobService.getJob(id);
      const freshData = response.data;

      // Update localStorage with fresh data
      localStorage.setItem(`${STORAGE_KEY}_${id}`, JSON.stringify(freshData));
      
      setJobData(freshData);
      setGeneratedContent(freshData.generatedContent);

      // Fetch effective pipeline to build dynamic tabs
      try {
        const pRes = await api.get(`/jobs/${id}/effective-pipeline`);
        setEffectivePipeline(pRes.data?.data || null);
      } catch (e) {
        console.warn('Could not load effective pipeline', e);
      }
    } catch (err) {
      setError("Failed to load job details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update localStorage when content changes
  useEffect(() => {
    if (generatedContent && jobData?._id) {
      const updatedData = {
        ...jobData,
        generatedContent
      };
      localStorage.setItem(`${STORAGE_KEY}_${jobData._id}`, JSON.stringify(updatedData));
    }
  }, [generatedContent, jobData]);

  useEffect(() => {
    if (id) {
      fetchJobData();
    }
  }, [id]);

  const handleGenerateContent = async () => {
    if (!inputContent.trim()) return;

    setIsGenerating(true);
    try {
      // Prepare existing content for continuation
      const existingContent = generatedContent ? {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      } : null;

      const response = await jobService.continueJobDescription(id, {
        user_message: inputContent.trim(), // Only send if not empty
        existing_content: existingContent
      });

      console.log('Full response:', response);
      console.log('Response type:', typeof response);
      console.log('Response.content:', response?.content);
      console.log('Response.content.content:', response?.content?.content);
      console.log('Response.data:', response?.data);

      if (response?.content?.content) {
        const newContent = response.content.content;
        setGeneratedContent({
          overview: newContent.overview,
          qualifications: newContent.qualifications,
          responsibilities: newContent.responsibilities,
          requirements: newContent.requirements
        });
        setInputContent("");
      } else if (response?.data?.content) {
        // Try alternative response structure
        const newContent = response.data.content;
        setGeneratedContent({
          overview: newContent.overview,
          qualifications: newContent.qualifications,
          responsibilities: newContent.responsibilities,
          requirements: newContent.requirements
        });
        setInputContent("");
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Received invalid response format from the service');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        err.message || 
        "Failed to enhance job description. Please try again later."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveContent = async (shouldContinue = false) => {
    // If there's no new generated content but user clicked Save & Continue, just navigate
    if (!generatedContent) {
      if (shouldContinue) handleTabChange("Media Plan & Content");
      return;
    }

    setIsSaving(true);
    try {
      await jobService.updateJobDescription(id, {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      });

      // Update localStorage after successful save
      const updatedData = {
        ...jobData,
        jobDescription: {
          ...jobData.jobDescription,
          overview: generatedContent.overview,
          generatedQualifications: generatedContent.qualifications,
          generatedResponsibilities: generatedContent.responsibilities,
          requirements: generatedContent.requirements
        }
      };
      localStorage.setItem(`${STORAGE_KEY}_${id}`, JSON.stringify(updatedData));

      // Refresh job data to show updated content
      await fetchJobData();
      
      setGeneratedContent(null);

      if (shouldContinue) {
        handleTabChange("Media Plan & Content");
      }
    } catch (err) {
      setError("Failed to save content. Please try again.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };


  
  // ── Dynamic tabs based on effective pipeline ─────────────────────────────
  const isCustomPipeline = effectivePipeline?.isCustom === true;
  const pipelineStages   = effectivePipeline?.stages || [];

  const tabs = [
    "Job Description",
    "Media Plan & Content",
    "Assessment Pipeline",
    "Resumes",
    // Only show stage tabs when a custom pipeline is explicitly configured
    ...(isCustomPipeline
      ? pipelineStages.filter(s => s.enabled !== false).map(s => s.label.replace(/\s*\(Arya\)/gi, ''))
      : []
    ),
    "Offer Letter",
  ];

  const renderTabContent = () => {
    // Fixed tabs
    switch (activeTab) {
      case "Assessment Pipeline":
        return <PipelineBuilder jobId={id} onSaved={() => {
          // Re-fetch pipeline so tabs update immediately after saving
          api.get(`/jobs/${id}/effective-pipeline`)
            .then(r => setEffectivePipeline(r.data?.data || null))
            .catch(() => {});
        }} />;
      case "Job Description":
        return <JobDescription jobData={jobData} generatedContent={generatedContent}
          handleGenerateContent={handleGenerateContent} handleSaveContent={handleSaveContent}
          isGenerating={isGenerating} isSaving={isSaving} inputContent={inputContent}
          setInputContent={setInputContent} setIsGenerating={setIsGenerating} setIsSaving={setIsSaving} />;
      case "Media Plan & Content":
        return <MediaPlanContent />;
      case "Resumes":
        return <ResumeManager />;
      case "Questionnaire":
        return <JobQuestionnaire />;
      case "Offer Letter":
        return <OfferLetterPage jobId={id} />;
      default:
        break;
    }

    // Pipeline stage tabs — match by label (normalize display-only strips like "(Arya)")
    const stage = pipelineStages.find(s => s.label.replace(/\s*\(Arya\)/gi, '') === activeTab);
    if (stage) return <PipelineStageTab stage={stage} />;

    return <div className="py-8 text-center text-gray-500">Select a tab</div>;
  };

  const renderHeader = () => (
    <div className="px-6 py-4">
      {jobData && (
        <>
          <div className="flex items-center gap-4 mb-4">
            <h1 className="text-2xl font-bold">{jobData.title}</h1>
            <span className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded-full">
              {jobData.status}
            </span>
            <button
              onClick={() => navigate(`/jobs/${jobData._id}/reports`)}
              className="ml-auto px-3 py-1.5 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900"
            >
              View assessment reports →
            </button>
          </div>
          <div className="flex gap-4">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              {jobData.department}
            </span>
            {/* <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              Full time
            </span> */}
          </div>
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <GradientDetailLayout>


    <div className=" max-h-screen min-h-screen overflow-hidden h-full  bg-transparent">
      <div className="border-b">
        {renderHeader()}
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      <div className="p-6 h-full overflow-y-auto max-h-[calc(100vh-150px)]">{renderTabContent()}</div>
    </div>
    </GradientDetailLayout>
  );
};

export default JobDetail;
